import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllPlans } from '../config/plans';
import api from '../api/client';
import './AdminSettings.css';

// Get the main platform URL based on environment
const getMainPlatformUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  return 'https://ai.app.platform.halisoft.biz';
};

const AdminSettings = () => {
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState('risk-analysis'); // 'risk-analysis' | 'hali-leads' | 'contract-analyzer'
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Optional deep-link: /admin/settings?module=hali-leads
    const urlParams = new URLSearchParams(window.location.search);
    const m = urlParams.get('module');
    if (m === 'hali-leads' || m === 'risk-analysis' || m === 'contract-analyzer' || m === 'invoice-financing-check' || m === 'past-trade-studio') {
      setActiveModule(m);
    }
    loadPlans(m === 'hali-leads' || m === 'risk-analysis' || m === 'contract-analyzer' || m === 'invoice-financing-check' || m === 'past-trade-studio' ? m : activeModule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async (module = activeModule) => {
    setLoading(true);
    try {
      // Ensure admin email is persisted when coming from magic link (?email=...)
      const urlParams = new URLSearchParams(window.location.search);
      const urlEmail = urlParams.get('email');
      if (urlEmail) {
        localStorage.setItem('halisoft_admin_email', urlEmail);
      }

      // First try to load from database via API (module-aware)
      const response = await api.get('/api/admin/plans', { params: { module } });
      if (response.data?.success && response.data?.plans?.length > 0) {
        // Get default features from config file
        const defaultPlans = getAllPlans();
        const defaultPlansMap = new Map(defaultPlans.map(p => [p.id, p]));
        
        // Transform DB format to frontend format
        const dbPlans = response.data.plans.map(plan => {
          // Use slug as id (string) instead of numeric id
          const planSlug = plan.slug || plan.id?.toString() || 'free';
          const defaultPlan = defaultPlansMap.get(planSlug);
          
          // Use DB features if available, otherwise fallback to config file features
          const features = Array.isArray(plan.features) && plan.features.length > 0
            ? plan.features
            : (defaultPlan?.features || []);
          
          return {
            id: planSlug, // Use slug (string) as id for frontend compatibility
            dbId: plan.id, // Keep numeric id for API calls
            name: plan.name,
            displayName: plan.name + ' Plan',
            description: plan.description || defaultPlan?.description || '',
            price: parseFloat(plan.price) || (defaultPlan?.price || 0),
            currency: plan.currency || defaultPlan?.currency || 'EUR',
            module: plan.module || module || 'global',
            quota: {
              limit: plan.quota || plan.quota_limit || defaultPlan?.quota?.limit || 100,
              resetPeriod: 'monthly'
            },
            features: features,
            setupFee: plan.setup_fee !== undefined ? parseFloat(plan.setup_fee) : 1000, // Default $1000 USD
            display: {
              badge: planSlug === 'pro' || planSlug === 'professional' ? 'Best Value' : (planSlug === 'starter' ? 'Popular' : null),
              color: planSlug === 'pro' || planSlug === 'professional' ? '#f6ad55' : (planSlug === 'starter' ? '#667eea' : '#718096'),
              highlighted: planSlug === 'starter',
              order: planSlug === 'free' ? 1 : (planSlug === 'starter' ? 2 : (planSlug === 'pro' || planSlug === 'professional' ? 3 : 4))
            }
          };
        });
        setPlans(dbPlans.sort((a, b) => a.display.order - b.display.order));
        console.log('[AdminSettings] Loaded plans from database:', dbPlans.length);
        console.log('[AdminSettings] Plans with features:', dbPlans.map(p => ({ name: p.name, features: p.features })));
      } else {
        // Fallback to local config
        const allPlans = getAllPlans();
        setPlans(allPlans);
        console.log('[AdminSettings] Loaded plans from local config');
      }
    } catch (error) {
      console.error('[AdminSettings] Error loading plans from API, using local config:', error);
      const allPlans = getAllPlans();
      setPlans(allPlans);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlan = (plan) => {
    console.log('[AdminSettings] Editing plan:', plan);
    console.log('[AdminSettings] Plan features:', plan.features);
    setEditingPlan(plan.id);
    const featuresText = Array.isArray(plan.features) && plan.features.length > 0
      ? plan.features.join('\n')
      : '';
    console.log('[AdminSettings] Features text:', featuresText);
    setFormData({
      name: plan.name,
      displayName: plan.displayName,
      description: plan.description,
      price: plan.price,
      currency: plan.currency,
      quotaLimit: plan.quota?.limit || plan.quotaLimit || 100,
      features: featuresText,
      setupFee: plan.setupFee !== undefined ? plan.setupFee : 1000, // Default $1000 USD
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSavePlan = async () => {
    setSaving(true);
    setMessage(null);

    try {
      // Find the plan being edited to get its numeric ID
      const planBeingEdited = plans.find(p => p.id === editingPlan);
      const planId = planBeingEdited?.dbId || editingPlan; // Use numeric ID for API
      
      // Prepare data - ensure all fields are included
      const updatedPlan = {
        id: planId, // Use numeric ID for backend
        name: formData.name || planBeingEdited?.name || '',
        description: formData.description || planBeingEdited?.description || '',
        price: formData.price !== undefined && formData.price !== '' ? parseFloat(formData.price) : (planBeingEdited?.price || 0),
        currency: formData.currency || planBeingEdited?.currency || 'EUR',
        module: activeModule,
        quota: {
          limit: formData.quotaLimit !== undefined && formData.quotaLimit !== '' ? parseInt(formData.quotaLimit) : (planBeingEdited?.quota?.limit || 100),
          resetPeriod: 'monthly',
        },
        features: formData.features ? formData.features.split('\n').filter(f => f.trim()) : (planBeingEdited?.features || []),
        setupFee: formData.setupFee !== undefined && formData.setupFee !== '' ? parseFloat(formData.setupFee) : (planBeingEdited?.setupFee || 1000),
      };
      
      console.log('[AdminSettings] Saving plan:', updatedPlan);

      // Save to backend (this will update the config file)
      const response = await api.post('/api/admin/update-plan', updatedPlan);

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Plan updated successfully!' });
        loadPlans(activeModule);
        setEditingPlan(null);
        setFormData({});
      } else {
        setMessage({ type: 'error', text: 'Failed to update plan' });
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      setMessage({
        type: 'error',
        text: 'Error: ' + (error.response?.data?.message || error.message)
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingPlan(null);
    setFormData({});
    setMessage(null);
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
      localStorage.removeItem('halisoft_user');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div className="admin-settings">
      {/* Header */}
      <header className="admin-settings-header">
        <div className="header-content">
          <div className="header-left">
            <div 
              className="logo" 
              onClick={() => window.location.href = `${getMainPlatformUrl()}/halilab/dashboard`}
              style={{ cursor: 'pointer', marginRight: '1rem' }}
            >
              <img src="/logo-halilab.svg" alt="HaliLab" style={{height: '36px'}} />
            </div>
            <button onClick={() => navigate('/admin')} className="btn-back">
              ← Back to Admin
            </button>
            <h1>⚙️ Admin Settings - Plans & Pricing</h1>
          </div>
          <button onClick={handleLogout} className="btn-logout">
            Logout
          </button>
        </div>
      </header>

      {/* Message */}
      {message && (
        <div className={`message message-${message.type}`}>
          {message.type === 'success' ? '✅' : '❌'} {message.text}
          <button onClick={() => setMessage(null)} className="message-close">✕</button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>⏳ Loading plans from database...</p>
        </div>
      )}

      {/* Plans List */}
      {!loading && (
      <div className="settings-container">
        <div className="settings-intro">
          <h2>Manage Subscription Plans</h2>
          <p>Configure pricing, quotas, and features for each plan. Changes will be applied immediately.</p>
        </div>

        {/* Module Tabs (Risk / Leads / Contract) */}
        <div className="module-tabs">
          <button
            className={`module-tab ${activeModule === 'risk-analysis' ? 'active' : ''}`}
            onClick={() => {
              setActiveModule('risk-analysis');
              setEditingPlan(null);
              setFormData({});
              loadPlans('risk-analysis');
            }}
          >
            📊 HaliCredit
          </button>
          <button
            className={`module-tab ${activeModule === 'hali-leads' ? 'active' : ''}`}
            onClick={() => {
              setActiveModule('hali-leads');
              setEditingPlan(null);
              setFormData({});
              loadPlans('hali-leads');
            }}
          >
            🎯 HaliLeads
          </button>
          <button
            className={`module-tab ${activeModule === 'contract-analyzer' ? 'active' : ''}`}
            onClick={() => {
              setActiveModule('contract-analyzer');
              setEditingPlan(null);
              setFormData({});
              loadPlans('contract-analyzer');
            }}
          >
            📜 Contract Analyzer
          </button>
          <button
            className={`module-tab ${activeModule === 'invoice-financing-check' ? 'active' : ''}`}
            onClick={() => {
              setActiveModule('invoice-financing-check');
              setEditingPlan(null);
              setFormData({});
              loadPlans('invoice-financing-check');
            }}
          >
            📋 HaliVerify
          </button>
          <button
            className={`module-tab ${activeModule === 'past-trade-studio' ? 'active' : ''}`}
            onClick={() => {
              setActiveModule('past-trade-studio');
              setEditingPlan(null);
              setFormData({});
              loadPlans('past-trade-studio');
            }}
          >
            📂 HaliPastTrade
          </button>
        </div>

        <div className="plans-grid">
          {plans.map((plan) => (
            <div key={plan.id} className="plan-card">
              {editingPlan === plan.id ? (
                // EDIT MODE
                <div className="plan-edit-form">
                  <h3>✏️ Editing: {(plan.slug || plan.name || String(plan.id)).toUpperCase()}</h3>

                  <div className="form-group">
                    <label>Plan Name</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="e.g., STARTER"
                    />
                  </div>

                  <div className="form-group">
                    <label>Display Name</label>
                    <input
                      type="text"
                      value={formData.displayName || ''}
                      onChange={(e) => handleInputChange('displayName', e.target.value)}
                      placeholder="e.g., Starter Plan"
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      value={formData.description || ''}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="e.g., For small teams"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>💰 Price</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price || ''}
                        onChange={(e) => handleInputChange('price', e.target.value)}
                        placeholder="9.99"
                      />
                    </div>

                    <div className="form-group">
                      <label>Currency</label>
                      <select
                        value={formData.currency || 'EUR'}
                        onChange={(e) => handleInputChange('currency', e.target.value)}
                      >
                        <option value="EUR">EUR (€)</option>
                        <option value="USD">USD ($)</option>
                        <option value="GBP">GBP (£)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>📊 Quota ({activeModule === 'hali-leads' ? 'leads' : activeModule === 'past-trade-studio' ? 'analyses' : 'checks'} per month)</label>
                    <input
                      type="number"
                      value={formData.quotaLimit || ''}
                      onChange={(e) => handleInputChange('quotaLimit', e.target.value)}
                      placeholder="1000"
                    />
                    <small>Set to -1 for unlimited</small>
                  </div>

                  <div className="form-group">
                    <label>💰 One-Time Setup Fee (USD)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.setupFee !== undefined ? formData.setupFee : 1000}
                      onChange={(e) => handleInputChange('setupFee', e.target.value)}
                      placeholder="1000"
                    />
                    <small>Set to 0 for no setup fee (only applies to paid plans)</small>
                  </div>

                  <div className="form-group">
                    <label>✨ Features (one per line)</label>
                    <textarea
                      value={formData.features || ''}
                      onChange={(e) => handleInputChange('features', e.target.value)}
                      placeholder="1,000 analyses per month&#10;Priority support&#10;API access"
                      rows="5"
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      onClick={handleSavePlan}
                      disabled={saving}
                      className="btn-save"
                    >
                      {saving ? '⏳ Saving...' : '💾 Save Changes'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={saving}
                      className="btn-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // VIEW MODE
                <div className="plan-view">
                  <div className="plan-header">
                    <span className={`plan-badge badge-${plan.id}`}>
                      {plan.name}
                    </span>
                    {plan.display.badge && (
                      <span className="plan-label">{plan.display.badge}</span>
                    )}
                  </div>

                  <h3>{plan.displayName}</h3>
                  <p className="plan-description">{plan.description}</p>

                  <div className="plan-pricing">
                    <div className="price-amount">
                      {plan.id === 'enterprise' || plan.name?.toLowerCase() === 'enterprise' || plan.price === null ? (
                        <span style={{ fontSize: '1.2rem', color: '#6366f1' }}>📞 Contact Sales</span>
                      ) : plan.price === 0 ? (
                        'FREE'
                      ) : (
                        <>
                          <span className="price-value">{plan.price}</span>
                          <span className="price-currency">{plan.currency}</span>
                          <span className="price-period">/month</span>
                        </>
                      )}
                    </div>
                    {/* One-time setup fee for paid plans */}
                    {plan.price > 0 && plan.id !== 'enterprise' && plan.name?.toLowerCase() !== 'enterprise' && (plan.setupFee === undefined || plan.setupFee > 0) && (
                      <div style={{
                        marginTop: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                        borderRadius: '8px',
                        border: '1px solid #fcd34d',
                        fontSize: '0.85rem',
                        color: '#92400e',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span>💰</span>
                        <span>+ ${(plan.setupFee !== undefined ? plan.setupFee : 1000).toLocaleString()} USD one-time setup fee</span>
                      </div>
                    )}
                  </div>

                  <div className="plan-quota">
                    <strong>📊 Quota:</strong>
                    {plan.quota?.limit === -1 || plan.quota?.limit === null || plan.id === 'enterprise' || plan.name?.toLowerCase() === 'enterprise' ? (
                      <span> Unlimited / Custom</span>
                    ) : (
                      <span> {plan.quota?.limit?.toLocaleString() || 0} {activeModule === 'hali-leads' ? 'leads' : activeModule === 'past-trade-studio' ? 'analyses' : 'checks'}/month</span>
                    )}
                  </div>

                  <div className="plan-features">
                    <strong>✨ Features:</strong>
                    {plan.features && plan.features.length > 0 ? (
                      <ul>
                        {plan.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    ) : (
                      <p style={{ fontStyle: 'italic', color: '#999', marginTop: '0.5rem' }}>No features defined</p>
                    )}
                  </div>

                  <button
                    onClick={() => handleEditPlan(plan)}
                    className="btn-edit-plan"
                  >
                    ✏️ Edit Plan
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div className="settings-info">
          <h3>ℹ️ Important Information</h3>
          <ul>
            <li><strong>Price Changes:</strong> New prices apply immediately to new subscriptions. Existing subscriptions keep their current price until renewal.</li>
            <li><strong>Quota Changes:</strong> Quota changes apply to all users on that plan immediately at next quota reset.</li>
            <li><strong>Features:</strong> Feature changes are cosmetic and update the pricing page immediately.</li>
            <li><strong>Backup:</strong> All changes are saved to the database.</li>
          </ul>
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminSettings;
